import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuth } from '../../../common/auth/src'
import { useHasMinsRole } from '../../../common/rbac/src'
import { NotAuthorizedPage } from '../../pages/NotAuthorizedPage'
import { ReportsPage } from '../../features/reports'

export const Route = createFileRoute('/_authenticated/reports')({
  component: ReportsRoute,
})

// Route guard decision for /reports (MEC-3250 §6 / acceptance criteria):
//   1. Not authenticated -> handled upstream by keycloak-js's
//      `login-required` (AuthProvider, mounted in _authenticated.tsx) —
//      nothing to do here.
//   2. Authenticated, no MINS role -> 'unauthorized'.
//   3. Has a MINS role but `tenantIds` is empty -> also 'unauthorized'
//      ("Utilisateur sans rôle MINS ou tenantIds vide -> NotAuthorizedPage").
//   4. Has a MINS role and a non-empty `tenantIds`, but the current
//      (subdomain-derived) tenant isn't in it -> 'crossTenantRedirect' to
//      tenantIds[0]'s subdomain.
//   5. Otherwise -> 'authorized'.
//
// Pure and exported so it's directly unit-testable (see
// tests/-reports.guard.test.ts) without rendering the route or a router.
//
// Deliberately NOT a TanStack Router `beforeLoad`: this repo's common/rbac
// only exposes `useHasMinsRole()` as a React hook (no non-hook store
// accessor is exported — see common/rbac/FEATURE.md: "useRbacStore /
// getRbacState are not exported from index.ts"), and `tenantIds` comes from
// `useAuth().user.tenantIds`, also hook-only. Neither can be read from
// `beforeLoad`, which runs outside React's render — so the decision is made
// in the route's component instead, using both hooks normally.
export type ReportsAccessDecision =
  | { type: 'unauthorized' }
  | { type: 'crossTenantRedirect'; url: string }
  | { type: 'authorized' }

export function resolveReportsAccess(params: {
  hasMinsRole: boolean
  // Full hostname, not just the tenant label — the redirect below needs to
  // preserve whatever comes after the tenant (e.g.
  // "tarification.qa2.majorconnect.ca"), which differs per environment and
  // isn't representable by a pre-parsed tenant string alone (code review
  // finding on MEC-3250's MR: the previous version hardcoded
  // "majorinsurers.ca" directly — wrong domain entirely — and dropped the
  // "tarification.<env>" segment on top of that).
  currentHostname: string
  tenantIds: string[]
}): ReportsAccessDecision {
  const { hasMinsRole, currentHostname, tenantIds } = params
  const [currentTenant = '', ...hostnameSuffixParts] = currentHostname.split('.')

  if (!hasMinsRole || tenantIds.length === 0) {
    return { type: 'unauthorized' }
  }

  if (tenantIds.includes(currentTenant)) {
    return { type: 'authorized' }
  }

  // Real cross-subdomain navigation, not a router.navigate — matches how
  // the rest of this auth flow already does full navigations for
  // cross-tenant moves (e.g. createAuthRedirectHandler in AppLayout.tsx).
  // Swap only the tenant label, keep the current hostname's own suffix
  // (including its env segment) intact rather than reconstructing a domain
  // from scratch — this way it's automatically correct in every environment.
  const url = `https://${tenantIds[0]}.${hostnameSuffixParts.join('.')}/reports`
  return { type: 'crossTenantRedirect', url }
}

function ReportsRoute() {
  const { user, isLoading } = useAuth()
  const hasMinsRole = useHasMinsRole()

  const decision = resolveReportsAccess({
    hasMinsRole,
    currentHostname: window.location.hostname,
    tenantIds: user?.tenantIds ?? [],
  })
  const redirectUrl = decision.type === 'crossTenantRedirect' ? decision.url : null

  useEffect(() => {
    if (redirectUrl) {
      window.location.href = redirectUrl
    }
  }, [redirectUrl])

  // Auth is still resolving (keycloak-js init/token parse) — RbacProvider
  // hasn't set the role from it yet either, so `hasMinsRole` would be a
  // false negative here. Render nothing rather than flashing
  // NotAuthorizedPage while that settles.
  if (isLoading) return null

  if (decision.type === 'unauthorized') return <NotAuthorizedPage />
  if (decision.type === 'crossTenantRedirect') return null

  return <ReportsPage />
}
